import axios from "axios";
import Vue from "vue";
import Toast from "vue-toastification";

const getApiEndPoint = (mode) => {
  mode = "" + mode.toLowerCase().trim();
  const links = {
    'production': "https://compliance-api.tectalik.com",
    'development': "https://compliance-api-staging.tectalik.com",
    'lan': "http://192.168.0.120:4001",
    'lan_secure': "https://192.168.0.120:4001",
  };
  return links[mode] ?? "http://localhost:4001";
};

const getSsoApiEndPoint = (mode) => {
  mode = "" + mode.toLowerCase().trim();
  const links = {
    'production': "https://sso.starrboard.com",
    'development': "https://sso.staging.starrboard.com",
    'lan': "http://localhost:4040",
    'lan_secure': "https://localhost:4040",
  };
  return links[mode] ?? "http://localhost:4040";
};

const api_end_point = getApiEndPoint(process.env.NODE_ENV);
const sso_api_end_point = getSsoApiEndPoint(process.env.NODE_ENV);
const uri = `${api_end_point}/tapi`;
const public_uri = `${api_end_point}/api`;

// Main API instance
const api = axios.create({
  baseURL: `${uri}`,
  method: "POST",
  headers: {
    Accept: "application/json",
  },
});

// Public API instance
const public_api = axios.create({
  baseURL: `${public_uri}`,
  method: "POST",
  headers: {
    Accept: "application/json",
  },
});

// SSO API instance
const sso_api = axios.create({
  baseURL: `${sso_api_end_point}`,
  method: "POST",
  headers: {
    Accept: "application/json",
  },
});

// Add headers to every request
const addHeaders = (config) => {
  let csrf_token = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content");
  if (csrf_token) {
    config.headers["X-CSRF-TOKEN"] = csrf_token;
  }

  let auth_token = localStorage.getItem("TOKEN");
  if (auth_token) {
    config.headers.Authorization = "Bearer " + auth_token;
  }

  const user_id = localStorage.getItem("USER_ID");
  if (user_id) {
    config.transformRequest = [
      (data) => {
        if (data && !data.from_user) {
          data.from_user = parseInt(user_id);
        }
        return data;
      },
      ...axios.defaults.transformRequest,
    ];
  }
  return config;
};

// Toast plugin
Vue.use(Toast, {
  transition: "Vue-Toastification__slideBlurred",
  maxToasts: 4,
  newestOnTop: true,
});

const global_push_toast = function() {
  Vue.prototype.$toast.error("You are offline!", { timeout: 5000 });
}.bind(this);

// PDF generation service (Puppeteer — https://pdf-service.tectalik.com)
const pdf_gen_api = axios.create({
  baseURL: 'https://pdf-service.tectalik.com/',
  method: 'POST',
  headers: { Accept: 'application/json' },
});

// Attach interceptors
api.interceptors.request.use(addHeaders);
public_api.interceptors.request.use(addHeaders);
sso_api.interceptors.request.use(addHeaders);

// 402 Payment Required — two flavours:
//   ERR_MODULE_*          → module/transaction limit; let it through so the component
//                           can show a targeted toast (do NOT redirect).
//   trial_expired         → trial ended; redirect with ?reason=trial_expired
//   subscription_expired  → renewal due; redirect with ?reason=subscription_expired
//   anything else         → generic payment wall; redirect.
const handle402 = (error) => {
  if (error?.response?.status === 402) {
    const code = error?.response?.data?.code || '';
    const errorCode = error?.response?.data?.error || '';
    if (code.startsWith('ERR_MODULE_')) {
      // Pass through — component .catch() will show the limit toast
      return Promise.reject(error);
    }
    localStorage.setItem('PAYMENT_BLOCKED', 'true');
    if (window.location.pathname !== '/payment-required') {
      const reason = errorCode || 'payment_required';
      window.location.href = `/payment-required?reason=${reason}`;
    }
    return Promise.resolve({ data: {} }); // swallow subscription 402
  }
  return Promise.reject(error);
};
api.interceptors.response.use(null, handle402);

// 403 Access Denied — entity/screen mismatch. The backend re-derives the
// active entity from the signed token (AuthorizeRequest plug); an ERR_AUTH_002
// means the request's entity_id no longer matches what this session is bound
// to (tampered localStorage, or a stale tab after switching). The cached
// session is inconsistent, so surface the error and force a clean re-login.
const handle403 = (error) => {
  if (error?.response?.status === 403) {
    const code = error?.response?.data?.code || '';
    if (code === 'ERR_AUTH_002' || code === 'ERR_AUTH_003') {
      const msg =
        error?.response?.data?.message ||
        'Access denied — your session is out of sync. Please sign in again.';
      try {
        Vue.prototype.$toast && Vue.prototype.$toast.error(msg, { timeout: 5000 });
      } catch (e) { /* toast is best-effort */ }
      // Avoid a redirect loop if we're already heading to login.
      // if (!/\/login/i.test(window.location.pathname)) {
      //   localStorage.clear();
      //   window.location.href = '/login?reason=session_reset';
      // }
      return Promise.reject(error);
    }
  }
  return Promise.reject(error);
};
api.interceptors.response.use(null, handle403);

// 401 Unauthorized — this token was superseded (e.g. the entity was switched in
// another tab/device, which invalidates the previous entity's token server-side),
// or it expired / was logged out. The token is dead → clear and re-login cleanly.
let session_ended = false;

const handle401 = (error) => {
  if (error?.response?.status === 401) {
    // Only end the session if the token that FAILED is the one we are still
    // using. A request that was already in flight when the token was re-minted
    // (entity switch) comes back 401 carrying the OLD token — that is expected
    // and must not log the user out, otherwise sessions drop at random.
    const used = (error?.config?.headers?.Authorization || '').replace(/^Bearer\s+/i, '').trim();
    const current = (localStorage.getItem('TOKEN') || '').trim();
    if (used && current && used !== current) {
      return Promise.reject(error);
    }

    // Some APPLICATION errors are (incorrectly) returned as 401 — e.g.
    // EnforceModuleLimit answers ERR_CLIENT_NOT_FOUND with 401 on FMS screens.
    // Those say nothing about the session, so they must surface as a normal
    // request failure instead of throwing the user out. Genuine auth failures
    // are Guardian's (no business `code`) or the session plug's SESSION_EXPIRED.
    const code = error?.response?.data?.code;
    if (code && code !== 'SESSION_EXPIRED') {
      return Promise.reject(error);
    }

    // Guard against several concurrent 401s each firing a redirect.
    if (session_ended || /\/login/i.test(window.location.pathname)) {
      return Promise.reject(error);
    }
    session_ended = true;

    try {
      Vue.prototype.$toast &&
        Vue.prototype.$toast.error('Your session ended. Please sign in again.', { timeout: 5000 });
    } catch (e) { /* toast is best-effort */ }
    localStorage.clear();
    window.location.href = '/login?reason=session_expired';
    return Promise.reject(error);
  }
  return Promise.reject(error);
};
api.interceptors.response.use(null, handle401);

axios.interceptors.request.use((config) => {
  if (navigator.onLine) {
    return config;
  } else {
    api.interceptors.request.use(global_push_toast);
    public_api.interceptors.request.use(global_push_toast);
    sso_api.interceptors.request.use(global_push_toast);
    return Promise.reject(new Error("You are offline!"));
  }
});

export { api, public_api, api_end_point, sso_api, pdf_gen_api };
